import React, { useEffect, useState } from 'react'

interface FilterBarProps {
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  children?: React.ReactNode
  onReset?: () => void
  showReset?: boolean
  className?: string
  searchClassName?: string
}

export function FilterBar({
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  children,
  onReset,
  showReset = false,
  className = '',
  searchClassName = '',
}: FilterBarProps) {
  const [localSearch, setLocalSearch] = useState(searchValue)

  // Sync state if prop changes from outside
  useEffect(() => {
    setLocalSearch(searchValue)
  }, [searchValue])

  // Debounced search trigger
  useEffect(() => {
    if (!onSearchChange) {
      return undefined
    }

    const handler = setTimeout(() => {
      if (localSearch !== searchValue) {
        onSearchChange(localSearch)
      }
    }, 300) // 300ms debounce delay

    return () => {
      clearTimeout(handler)
    }
  }, [localSearch, onSearchChange, searchValue])

  return (
    <div className={`bg-surface border border-border p-3.5 sm:p-4 rounded-card mb-6 shadow-sm ${className}`}>
      <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
        {/* Search Input Box */}
        {onSearchChange !== undefined && (
          <div
            className={`relative min-w-0 ${
              searchClassName || (children ? 'w-full sm:w-80 md:w-96 lg:w-[440px]' : 'w-full sm:flex-1 sm:max-w-md')
            }`}
          >
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text-secondary pointer-events-none">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </span>
            <input
              type="text"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full h-9 pl-9 pr-8 border border-border rounded-btn bg-bg text-text-primary text-xs sm:text-sm font-medium placeholder-text-secondary focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none transition-all duration-150"
            />
            {localSearch && (
              <button
                type="button"
                onClick={() => {
                  setLocalSearch('')
                  if (onSearchChange) onSearchChange('')
                }}
                className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-text-secondary hover:text-text-primary focus:outline-none"
                title="Clear search"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Custom selectors and inputs slot */}
        {children}

        {/* Reset Filter Button */}
        {showReset && onReset && (
          <button
            onClick={onReset}
            className="h-9 w-full sm:w-auto sm:ml-auto flex items-center justify-center gap-1.5 px-3 text-xs sm:text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-danger/5 hover:border-danger/30 text-text-secondary hover:text-danger transition-colors whitespace-nowrap cursor-pointer"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3 3m0 0l3-3m-3 3V4"
              />
            </svg>
            Reset Filters
          </button>
        )}
      </div>
    </div>
  )
}
